import CTNButton from '@ctn/ui/components/ctn-button';
import CTNCheckBox from '@ctn/ui/components/ctn-checkbox';
import { type CTNCheckBoxProps } from '@ctn/ui/types/ctn-checkbox';
import classNames from 'classnames';
import { useMemo, type ReactNode } from 'react';
import { type FieldValues } from 'react-hook-form';

const classNameComponent = 'c-pnp-checkbox-group';

// --- 1. COMPONENTE CONTENEDOR (El ul principal) ---
type CheckboxGroupProps = {
	readonly children: ReactNode;
	readonly className?: string;
	readonly title: string;
	readonly labelButton?: string;
	readonly onButtonClick?: () => void;
};

export const CheckboxGroup = ({ children, title, className, labelButton, onButtonClick }: CheckboxGroupProps) => {
	const classNameCheckboxList = useMemo(() => classNames(classNameComponent, className), [className]);
	return (
		<fieldset className={classNameCheckboxList}>
			<legend className={`${classNameComponent}__header c-full-width`}>
				<div className='c-div-group'>
					<label>{title}</label>
					<CTNButton
						mode="secondary"
						variant='outline'
						label={labelButton ?? 'Seleccionar todo'}
						size="size-32"
						minWidth='140'
						onClick={() => {
							onButtonClick?.();
						}}
					/>
				</div>
			</legend>
			
			<ul className={`${classNameComponent}__grid`}>{children}</ul>
		</fieldset>
	);
};

// --- 2. COMPONENTE HIJO (El li de las notificaciones) ---
type ItemProps< T extends FieldValues> = {
	readonly control: CTNCheckBoxProps<T>['control'];
	readonly name: CTNCheckBoxProps<T>['name'];
	readonly onChange?: CTNCheckBoxProps<T>['onChange'];
	readonly label: string;
	readonly value: string | number;
	readonly className?: string;
};

const CheckboxGroupSubItem = < T extends FieldValues>({ control, name, label, value, className, onChange }: ItemProps<T>) => {
	const classNameCheckboxGroupSubItem = useMemo(
		() => classNames(`${classNameComponent}__row`, `${classNameComponent}__row--subitem`, className),
		[className],
	);
	return (
		<li className={classNameCheckboxGroupSubItem}>
			<CTNCheckBox
				noFeedBack
				classNameField={`${classNameComponent}__checkbox`}
				checkbox={{ large: true }}
				control={control}
				name={name}
				items={[{ label, value }]}
				onChange={onChange}
			/>
		</li>
	);
};

// --- 3. COMPONENTE GRUPO (El padre que agrupa el check principal y sus hijos) ---
type GroupProps< T extends FieldValues>  = {
	readonly control: CTNCheckBoxProps<T>['control'];
	readonly name: CTNCheckBoxProps<T>['name'];
	readonly onChange?: CTNCheckBoxProps<T>['onChange'];
	readonly label: string;
	readonly value: number | string;
	readonly children?: ReactNode;
	readonly isExpanded: boolean;
	readonly className?: string;
	readonly labelButton?: string;
	readonly onButtonClick?: () => void;
};

const CheckboxGroupItem = < T extends FieldValues>({ control, className, name, label, children, value, isExpanded, labelButton, onChange, onButtonClick }: GroupProps<T>) => {
	const classNameCheckboxGroupItem = useMemo(
		() => classNames(`${classNameComponent}__row`, `${classNameComponent}__row--item`, className),
		[className],
	);

	return (
		<li className={classNameCheckboxGroupItem}>
			<div className='c-div-group'>
				<CTNCheckBox
					noFeedBack
					layout="inline"
					classNameField={`${classNameComponent}__checkbox`}
					checkbox={{ large: true }}
					control={control}
					name={name}
					items={[{ label, value }]}
					onChange={onChange}
				/>

				{isExpanded && (
					<CTNButton
						mode="secondary"
						variant='ghost'
						label={labelButton ?? 'Seleccionar todo'}
						size="size-32"
						minWidth='140'
						onClick={() => {
							onButtonClick?.();
						}}
					/>
				)}
			</div>

			{children && (
				<ul
					className={classNames(`${classNameComponent}__collapsable`, {
						[`${classNameComponent}__collapsable--show`]: isExpanded,
					})}
				>
					{children}
				</ul>
			)}
		</li>
	);
};

// --- 4. VINCULAMOS TODO AL COMPONENTE PRINCIPAL ---
CheckboxGroup.SubItem = CheckboxGroupSubItem;
CheckboxGroup.Item = CheckboxGroupItem;
